import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap-5';
import TiptapEditor from '../../rich_text_editor/tiptap_editor';
import { initializeEditor } from '../../rich_text_editor/helpers';
import { Editor } from '@tiptap/core';
import type { MailersHeader } from 'bundles/connect/types';
import MailersHeaderPreview from './mailers_header_preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import { deleteMailersLogo, updateMailersHeader } from 'bundles/connect/helpers/prospect_api';

type MailersHeaderEditModalProps = {
  header: MailersHeader;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  setHeader?: React.Dispatch<React.SetStateAction<MailersHeader>>;
}

export default function MailersHeaderEditModal({ header, show, setShow, setHeader }: MailersHeaderEditModalProps) {
  const [leftLetterhead, setLeftLetterhead] = useState(header.htmlLeft);
  const leftEditorRef = useRef(null);
  const leftTiptapInstance = useRef<Editor | null>(null);
  const [rightLetterhead, setRightLetterhead] = useState(header.htmlRight);
  const rightEditorRef = useRef(null);
  const rightTiptapInstance = useRef<Editor | null>(null);
  const [logoUrl, setLogoUrl] = useState(header.logoUrl);
  const [loading, setLoading] = useState(false);

  const onHide = () => {
    setShow(false);
    if (leftTiptapInstance.current) {
      leftTiptapInstance.current.destroy();
      leftTiptapInstance.current = null;
    }
    if (rightTiptapInstance.current) {
      rightTiptapInstance.current.destroy();
      rightTiptapInstance.current = null;
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(String(reader.result));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    deleteMailersLogo().then(() => {
      setLogoUrl(null);
      displayToast('Logo deleted Successfully', 'success');
    }).catch(() => {
      displayToast('Something went wrong, please try again', 'error');
    });
  };

  const onSubmit = () => {
    setLoading(true);
    const file = logoUrl ? (document.getElementById('logo-upload') as HTMLInputElement | null)?.files?.[0] : null;
    const formData = new FormData();
    formData.append('html_left', leftLetterhead);
    formData.append('html_right', rightLetterhead);
    formData.append('logo', file || '');
    if (header.id) {
      formData.append('id', header.id.toString());
    }

    updateMailersHeader(formData).then((res) => {
      onHide();
      displayToast('Letterhead successfully updated', 'success');
      setHeader && setHeader(res);
    }).catch(() => {
      displayToast('Something went wrong, please try again', 'error');
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (show) {
      if (leftEditorRef.current && !leftTiptapInstance.current) {
        initializeEditor(
          leftEditorRef,
          leftTiptapInstance,
          leftLetterhead,
          setLeftLetterhead
        );
      }

      if (rightEditorRef.current && !rightTiptapInstance.current) {
        initializeEditor(
          rightEditorRef,
          rightTiptapInstance,
          rightLetterhead,
          setRightLetterhead
        );
      }
    }
    void rightTiptapInstance.current?.commands.setTextAlign('right');
  }, [show]);

  useEffect(() => {
    setLeftLetterhead(header.htmlLeft);
    setRightLetterhead(header.htmlRight);
    setLogoUrl(header.logoUrl);
  }, [header]);

  return (
    <Modal centered show={show} size="xl" onHide={onHide}>
      <Modal.Header className="flex-row" closeButton>
        <Modal.Title as="h5" className="fw-bold">Letterhead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>The letterhead gets applied to every template.</p>
        <Row className="border-bottom pb-3">
          <Col xs={4}>
            <div className='d-flex flex-column h-100'>
              <div>
                <Button variant='outline' onClick={() => document.getElementById('logo-upload')?.click()}>
                  <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                  Upload Logo
                </Button>
                <Button className="ms-2" disabled={!logoUrl} variant="destructive" onClick={handleDeleteImage}>
                  Remove Logo
                </Button>
              </div>
              <input className='d-none' id="logo-upload" type='file' onChange={handleImageUpload}></input>
              <div
                id="logo-preview-container"
                className='border rounded bg-light mt-3 d-flex align-items-center justify-content-center'
                style={{ height: '-webkit-fill-available' }}
              >
                {logoUrl && <img id="logo-preview" src={logoUrl} style={{ maxHeight: '100px' }} />}
              </div>
            </div>
          </Col>
          <Col xs={4} className="d-flex flex-column">
            <strong>Letterhead Left</strong>
            <TiptapEditor
              editorRef={leftEditorRef}
              lite
              showDynamic
              tiptapInstance={leftTiptapInstance}
              maxHeight="18vh"
              extraClasses="flex-grow-1"
            />
          </Col>
          <Col xs={4} className="d-flex flex-column">
            <strong>Letterhead Right</strong>
            <TiptapEditor
              editorRef={rightEditorRef}
              lite
              showDynamic
              tiptapInstance={rightTiptapInstance}
              maxHeight="18vh"
              extraClasses="flex-grow-1"
            />
          </Col>
        </Row>
        <MailersHeaderPreview initialHeader={{ htmlLeft: leftLetterhead, htmlRight: rightLetterhead, logoUrl }} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onSubmit} disabled={loading}>
          <FontAwesomeIcon className="me-2" icon={faFloppyDisk} />
          Save Letterhead
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
